import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { ContactsAllResponse } from '../models/contacts';
import { ContactDetails } from '../components/contact-details/models/contact-details';
import { CreatePersonContactRequest } from '../components/add-person-contact/models/create-person-contact-request';
import {
  CreateDepartmentContactRequest,
  UpdateDepartmentContactRequest
} from '../components/add-department-contact/models/create-department-contact-request';
import { EditContactDetails, UpdateContactEditRequest } from '../components/edit-contact/models/edit-contact';

@Injectable({
  providedIn: 'root'
})
export class ContactsService {
  private http = inject(HttpClient);

  // Fetches every contact once (search/type filtering and sorting all happen client-side
  // afterwards, in ContactsComponent — see VIRTUAL_SCROLL.md).
  //
  // `pageSize: null` is what asks for the unpaged set — the endpoint was adjusted for it when
  // this grid moved to virtual scrolling. It has to be sent *explicitly*: an empty body leaves
  // the key absent, which lets the request's own default page size (20) stand and silently
  // clipped the grid to the first 20 rows. Null is the absence of a page size; omission is not.
  getAllContacts(): Observable<ContactsAllResponse> {
    return this.http.post<ContactsAllResponse>(`${environment.apiUrl}/supportContacts/search`, {
      pageSize: null
    });
  }

  save(contact: ContactDetails) {
    return this.http.post(`${environment.apiUrl}/supportContacts`, contact);
  }

  // Separate from save() because the Person request body is NOT a Partial<ContactDetails> —
  // it only ever carries `userId` (+ assignments/toggles); sending name/email/phone/etc. here
  // is a validation error (400), since those are copied server-side from the linked user.
  createPersonContact(request: CreatePersonContactRequest): Observable<ContactDetails> {
    return this.http.post<ContactDetails>(`${environment.apiUrl}/supportContacts`, request);
  }

  // Same endpoint as save(), but a narrower body: no id/userId/photoReference/lastSyncedAt, and
  // assignments are keyed by partyId. See CreateDepartmentContactRequest.
  createDepartmentContact(request: CreateDepartmentContactRequest): Observable<ContactDetails> {
    return this.http.post<ContactDetails>(`${environment.apiUrl}/supportContacts`, request);
  }

  // PUT counterpart to createDepartmentContact(), used when AddDepartmentContactComponent is
  // opened in edit mode. Unlike updateContact() below (the narrow Person-only body), the
  // Department path of this same {id} endpoint requires the full detail body — see the comment
  // on updateContact().
  updateDepartmentContact(id: number, request: UpdateDepartmentContactRequest): Observable<ContactDetails> {
    return this.http.put<ContactDetails>(`${environment.apiUrl}/supportContacts/${id}`, request);
  }

  // Hard delete — the API removes the contact and its party assignments. Contacts that should
  // only be hidden from customers are deactivated via the isActive flag instead.
  delete(id: number) {
    return this.http.delete(`${environment.apiUrl}/supportContacts/${id}`);
  }

  // Same GET/PUT {id} the general contact endpoints use — no dedicated edit-dialog route. The
  // detail fields this dialog never sends (displayName/email/phone/etc.) are simply omitted
  // from the PUT body; the backend treats a missing field the same as an explicit null, and
  // rejects (not just ignores) a non-null value there for a Person contact — same reasoning
  // createPersonContact() already relies on. This dialog is Person-only (see
  // ContactsComponent.editContact()), so the Update endpoint's Department path — which would
  // reject a null displayName/email — is never hit from here.
  getContactForEdit(id: number): Observable<EditContactDetails> {
    return this.http.get<EditContactDetails>(`${environment.apiUrl}/supportContacts/${id}`);
  }

  updateContact(id: number, data: UpdateContactEditRequest): Observable<EditContactDetails> {
    return this.http.put<EditContactDetails>(`${environment.apiUrl}/supportContacts/${id}`, data);
  }
}
